import {
  ApplicationDeniedIcon,
  EqualToIcon,
  GreaterThanIcon,
  InBetweenIcon,
  LessThanIcon,
  PayoutActionNeededIcon,
  PayoutHalfProgressIcon,
  PayoutInitialIcon,
  PayoutQuarterProgressIcon,
  ScheduledIcon,
  TickWithBlueBorderIcon,
  TxnFailedIcon,
  TxnInTransitIcon,
  TxnPausedIcon,
  TxnPendingIcon,
  TxnSuccessIcon,
  TxnTimedOutIcon
} from 'assets/icons';
import {
  ConnectPayoutStatus,
  EnablePayoutStatuses,
  HELD_IN_RESERVE_DATE_OPTIONS,
  PAYOUT_TXNS_AMOUNT_FILTERS,
  PAYOUT_TXNS_TYPE_FILTERS,
  PaymentSystem
} from './constants';
import DateUtils from 'utils/dateUtils';
import { AmountFilterOperation } from 'constants/common';
import { EntityFilters } from 'containers/app/app.model';
import { PayoutDistributionStatus } from './payouts.model';
import { store } from 'store/store';

export function formatStatus(status) {
  const words = status.toLowerCase().split('_');
  const formattedWords = [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)];
  const formattedStatus = formattedWords.join(' ');

  return formattedStatus;
}

export function getPayoutStatusData({ payoutStatus }: { payoutStatus: string }) {
  switch (payoutStatus) {
    case ConnectPayoutStatus.INITIATED:
      return {
        Icon: TxnPendingIcon,
        textColor: '#4F4F4F',
        text: 'Initiated'
      };
    case ConnectPayoutStatus.IN_TRANSIT:
      return {
        Icon: TxnInTransitIcon,
        textColor: '#3876BB',
        text: 'In transit'
      };
    case ConnectPayoutStatus.SUCCESS:
      return {
        Icon: TxnSuccessIcon,
        textColor: '#419364',
        text: 'Success'
      };
    case ConnectPayoutStatus.FAILED:
      return {
        Icon: TxnFailedIcon,
        textColor: '#D95945',
        text: 'Failed'
      };
    case ConnectPayoutStatus.CANCELLED:
      return {
        Icon: TxnFailedIcon,
        textColor: '#D95945',
        text: 'Cancelled'
      };
    case ConnectPayoutStatus.RETRYING:
      return {
        Icon: TxnTimedOutIcon,
        textColor: '#800000',
        text: 'Retry in progress'
      };
    case ConnectPayoutStatus.PAUSED:
      return {
        Icon: TxnPausedIcon,
        textColor: '#F2994A',
        text: 'Paused'
      };
    case ConnectPayoutStatus.SCHEDULED:
      return {
        Icon: ScheduledIcon,
        textColor: '#F2994A',
        text: 'Paused'
      };
    default:
      return {
        Icon: TxnPendingIcon,
        textColor: '#4F4F4F',
        text: formatStatus(payoutStatus)
      };
  }
}

export const getSubText = ({
  payoutStatus,
  arrivalAt,
  createdAt,
  updatedAt,
  isLIP
}: {
  payoutStatus: string;
  arrivalAt: string;
  createdAt: string;
  updatedAt: string;
  isLIP: boolean;
}) => {
  switch (payoutStatus) {
    case ConnectPayoutStatus.SUCCESS:
      if (isLIP) return `Completed by ${arrivalAt}`;
      if (arrivalAt === DateUtils.getToday('dd MMM yyyy')) {
        return `Expected today, ${arrivalAt}`;
      } else if (arrivalAt === DateUtils.getTomorrow('dd MMM yyyy')) return `Expected by tomorrow, ${arrivalAt}`;
      return `Completed by ${arrivalAt}`;

    case ConnectPayoutStatus.INITIATED:
    case ConnectPayoutStatus.IN_TRANSIT:
      if (arrivalAt === DateUtils.getToday('dd MMM yyyy')) {
        return `Expected today, ${arrivalAt}`;
      } else if (arrivalAt === DateUtils.getTomorrow('dd MMM yyyy')) return `Expected by tomorrow, ${arrivalAt}`;
      return `Expected by ${arrivalAt}`;

    case ConnectPayoutStatus.FAILED:
    case ConnectPayoutStatus.CANCELLED:
    case ConnectPayoutStatus.RETRYING:
    case ConnectPayoutStatus.PAUSED:
      return `Updated on ${updatedAt}`;

    case ConnectPayoutStatus.SCHEDULED:
      return createdAt;

    default:
      return createdAt;
  }
};

export const checkPermissions = ({
  paymentSystem,
  viewPayoutsBySelf,
  viewPayoutsByTeam
}: {
  paymentSystem: PaymentSystem;
  viewPayoutsBySelf: boolean;
  viewPayoutsByTeam: boolean;
}) => {
  if (!viewPayoutsByTeam) return false;
  if (paymentSystem === PaymentSystem.LIP) {
    if (!viewPayoutsBySelf && !viewPayoutsByTeam) return false;
    return true;
  }
  return true;
};

export const getEnablePayoutsIcon = status => {
  switch (status) {
    case EnablePayoutStatuses.NOT_STARTED:
    case EnablePayoutStatuses.STARTED_IN_PROGRESS:
      return PayoutInitialIcon;

    case EnablePayoutStatuses.SUBMITTED_IN_REVIEW:
      return PayoutQuarterProgressIcon;

    case EnablePayoutStatuses.SUBMITTED_NEED_MORE_PAYMENT_ALLOWED:
    case EnablePayoutStatuses.SUBMITTED_NEED_MORE_PAYMENT_NOT_ALLOWED:
      return PayoutActionNeededIcon;

    case EnablePayoutStatuses.APPROVED:
    case EnablePayoutStatuses.APPROVED_CONDITIONAL:
      return PayoutHalfProgressIcon;

    case EnablePayoutStatuses.REJECTED:
      return ApplicationDeniedIcon;

    default:
      return PayoutQuarterProgressIcon;
  }
};

export const getAmountFilterIcon = (filterTtile: PAYOUT_TXNS_AMOUNT_FILTERS) => {
  switch (filterTtile) {
    case PAYOUT_TXNS_AMOUNT_FILTERS.ALL:
      return { Icon: TickWithBlueBorderIcon };
    case PAYOUT_TXNS_AMOUNT_FILTERS.EQUAL_TO:
      return { Icon: EqualToIcon };
    case PAYOUT_TXNS_AMOUNT_FILTERS.GREATER_THAN:
      return { Icon: GreaterThanIcon };
    case PAYOUT_TXNS_AMOUNT_FILTERS.IN_BETWEEN:
      return { Icon: InBetweenIcon };
    case PAYOUT_TXNS_AMOUNT_FILTERS.LESS_THAN:
      return { Icon: LessThanIcon };
    default:
      return { Icon: TickWithBlueBorderIcon };
  }
};

export const getAmountFilterString = amount => {
  let amountFilterString = '';
  if (amount.title !== PAYOUT_TXNS_AMOUNT_FILTERS.ALL) {
    switch (amount.title) {
      case PAYOUT_TXNS_AMOUNT_FILTERS.EQUAL_TO:
        amountFilterString = `${AmountFilterOperation.EQ}:${amount.min}`;
        break;
      case PAYOUT_TXNS_AMOUNT_FILTERS.IN_BETWEEN:
        amountFilterString = `${AmountFilterOperation.BW}:${amount.min}:${amount.max}`;
        break;
      case PAYOUT_TXNS_AMOUNT_FILTERS.GREATER_THAN:
        amountFilterString = `${AmountFilterOperation.GT}:${amount.min}`;
        break;
      case PAYOUT_TXNS_AMOUNT_FILTERS.LESS_THAN:
        amountFilterString = `${AmountFilterOperation.LT}:${amount.min}`;
        break;
      default:
        break;
    }
  }
  return amountFilterString;
};

export const constructDateRange = (option: string) => {
  const today = new Date();
  const createdEndDate = new Date(today.setHours(23, 59, 59, 999)).toISOString();

  switch (option) {
    case HELD_IN_RESERVE_DATE_OPTIONS.CREATED.LAST_1_MONTH: {
      const startDate = new Date(today.setMonth(today.getMonth() - 1));
      startDate.setHours(0, 0, 0, 0);
      return { startDate: startDate.toISOString(), endDate: createdEndDate };
    }
    case HELD_IN_RESERVE_DATE_OPTIONS.CREATED.LAST_7_DAYS: {
      const startDate = new Date(today.setDate(today.getDate() - 7));
      startDate.setHours(0, 0, 0, 0);
      return { startDate: startDate.toISOString(), endDate: createdEndDate };
    }
    case HELD_IN_RESERVE_DATE_OPTIONS.CREATED.LAST_6_MONTHS: {
      const startDate = new Date(today.setMonth(today.getMonth() - 6));
      startDate.setHours(0, 0, 0, 0);
      return { startDate: startDate.toISOString(), endDate: createdEndDate };
    }
    case HELD_IN_RESERVE_DATE_OPTIONS.AVAILABLE.NEXT_1_MONTH: {
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(today.setMonth(today.getMonth() + 1));
      endDate.setHours(23, 59, 59, 999);
      return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    }
    case HELD_IN_RESERVE_DATE_OPTIONS.AVAILABLE.NEXT_7_DAYS: {
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(today.setDate(today.getDate() + 7));
      endDate.setHours(23, 59, 59, 999);
      return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    }
    case HELD_IN_RESERVE_DATE_OPTIONS.AVAILABLE.NEXT_6_MONTHS: {
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(today.setMonth(today.getMonth() + 6));
      endDate.setHours(23, 59, 59, 999);
      return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    }
    default:
      return { startDate: null, endDate: null };
  }
};

export const getPayoutTransactionFilters = (
  currentTab: string
): Array<{ key: string; label: string; isEnabled: boolean }> => {
  const { rootReducer } = store.getState();
  const { entityFilters } = rootReducer.app.entityFilters ?? {};
  const { payoutTransactions } = entityFilters ?? {};
  if (!payoutTransactions?.filters?.byType) {
    return [
      { key: PAYOUT_TXNS_TYPE_FILTERS.ALL, label: 'All', isEnabled: true },
      { key: PAYOUT_TXNS_TYPE_FILTERS.COLLECTED, label: 'Collected', isEnabled: true },
      { key: PAYOUT_TXNS_TYPE_FILTERS.FEES, label: 'Fees', isEnabled: true },
      { key: PAYOUT_TXNS_TYPE_FILTERS.REFUNDS, label: 'Refunds', isEnabled: true },
      { key: PAYOUT_TXNS_TYPE_FILTERS.ADJUSTMENTS, label: 'Adjustments', isEnabled: true },
      { key: PAYOUT_TXNS_TYPE_FILTERS.CREDITS, label: 'Credits', isEnabled: true }
    ];
  }

  const { filters, tabs } = payoutTransactions;
  const currentTabData = tabs.find(tab => tab.label.toLowerCase() === currentTab.toLowerCase());

  // If it's "All" tab or no specific tab found, return all enabled filters
  if (!currentTabData || currentTabData.applyOn.length === 0) {
    return filters.byType.filter(filter => filter.isEnabled);
  }

  // Filter based on current tab's applyOn array
  return filters.byType.filter(filter => filter.isEnabled && currentTabData.applyOn.includes(filter.key));
};

export const getPayoutTransactionTabs = (
  entityFilters: EntityFilters
): Array<{ label: string; applyOn: string[]; isEnabled: boolean }> => {
  if (!entityFilters?.payoutTransactions?.tabs) {
    return [];
  }

  return entityFilters.payoutTransactions.tabs.filter(tab => tab.isEnabled);
};

export const getPayoutTransactionTabsFilter = (currentTab: string) => {
  const { rootReducer } = store.getState();
  const { entityFilters } = rootReducer.app.entityFilters ?? {};
  const { payoutTransactions } = entityFilters ?? {};
  const { tabs } = payoutTransactions ?? {};
  const currentTabData = tabs.find(tab => tab.label.toLowerCase() === currentTab.toLowerCase());
  return currentTabData?.applyOn ?? [];
};

export const getPayoutDistributionLabel = (expectedBy: string | null, status: PayoutDistributionStatus): string => {
  if (!expectedBy || status === PayoutDistributionStatus.UNSCHEDULED) {
    return 'Unscheduled';
  }

  const date = DateUtils.parseISODateString(expectedBy);
  return DateUtils.getDateInFormat({ date, dateFormat: 'dd MMM yyyy' });
};
